import React, { useState } from 'react';
import './FeedbackPage.css';
import { useGetAdminFeedbackQuery, FeedbackItem } from 'store/services/feedback/feedbackApi';
import AdminLayout from '../AdminLayout';
import { FeedbackType } from 'store/services/feedback/feedbackApi';
import { AssetUrls } from 'config/assets';
import { getAvatarPlaceholder } from 'utils/avatarUtils';

const FeedbackPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [feedbackTypeFilter, setFeedbackTypeFilter] = useState<FeedbackType | 'all'>('all');
  const [page, setPage] = useState(1);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [viewedFeedbackIds, setViewedFeedbackIds] = useState<Set<string>>(() => {
    // Load viewed feedback IDs from localStorage
    const stored = localStorage.getItem('viewedFeedbackIds');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  const [hiddenFeedbackIds, setHiddenFeedbackIds] = useState<Set<string>>(() => {
    // Load hidden feedback IDs from localStorage
    const stored = localStorage.getItem('hiddenFeedbackIds');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });
  
  const { data, isLoading, error } = useGetAdminFeedbackQuery({
    page,
    pageSize: 50,
    search: searchTerm,
    feedbackType: feedbackTypeFilter === 'all' ? undefined : feedbackTypeFilter
  });

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFeedbackTypeLabel = (type: FeedbackType) => {
    switch (type) {
      case 'bug_report':
        return 'Bug Report';
      case 'feature_request':
        return 'Feature Request';
      case 'general':
        return 'General';
      default:
        return type;
    }
  };

  const getFeedbackTypeColor = (type: FeedbackType) => {
    switch (type) {
      case 'bug_report':
        return '#ef4444'; // Red
      case 'feature_request':
        return '#3b82f6'; // Blue  
      case 'general':
        return '#10b981'; // Green
      default:
        return '#999';
    }
  };

  const getSentimentEmoji = (sentiment?: string) => {
    switch (sentiment) {
      case 'frustrated':
        return '😤';
      case 'neutral':
        return '😐';
      case 'happy':
        return '😊';
      default:
        return '';
    }
  };

  const getOverviewText = (item: FeedbackItem) => {
    // Combine all fields for bug reports and feature requests
    if (item.feedbackType === 'bug_report') {
      const parts = [];
      if (item.overview) parts.push(`Overview: ${item.overview}`);
      if (item.currentBehavior) parts.push(`Current: ${item.currentBehavior}`);
      if (item.desiredBehavior) parts.push(`Desired: ${item.desiredBehavior}`);
      return parts.length > 0 ? parts.join(' | ') : 'No overview provided';
    } else if (item.feedbackType === 'feature_request') {
      const parts = [];
      if (item.featureOverview) parts.push(`Overview: ${item.featureOverview}`);
      if (item.coreProblem) parts.push(`Problem: ${item.coreProblem}`);
      if (item.proposedSolution) parts.push(`Solution: ${item.proposedSolution}`);
      return parts.length > 0 ? parts.join(' | ') : 'No overview provided';
    } else {
      return item.generalFeedback || 'No feedback provided';
    }
  };

  const handleViewFeedback = (item: FeedbackItem) => {
    setSelectedFeedback(item);
    setIsDetailModalOpen(true);
    
    // Mark as viewed
    if (!viewedFeedbackIds.has(item.id)) {
      const newViewedIds = new Set(viewedFeedbackIds);
      newViewedIds.add(item.id);
      setViewedFeedbackIds(newViewedIds);
      // Save to localStorage
      localStorage.setItem('viewedFeedbackIds', JSON.stringify(Array.from(newViewedIds)));
      // Dispatch custom event to update badge in sidebar
      window.dispatchEvent(new Event('feedbackViewed'));
    }
  };
  
  const handleHideFeedback = (id: string) => {
    const newHiddenIds = new Set(hiddenFeedbackIds);
    newHiddenIds.add(id);
    setHiddenFeedbackIds(newHiddenIds);
    // Save to localStorage
    localStorage.setItem('hiddenFeedbackIds', JSON.stringify(Array.from(newHiddenIds)));
    // Dispatch custom event to update badge in sidebar
    window.dispatchEvent(new Event('feedbackViewed'));
  };

  // Mock data for demo - replace with actual API data when backend is ready
  const mockFeedback: FeedbackItem[] = [
    {
      id: '1',
      userId: 1346134,
      user: {
        id: 1346134,
        username: 'SamuraiFunn',
        email: 'samuraifunn@gmail.com',
        avatar: AssetUrls.avatars.avatar1
      },
      feedbackType: 'bug_report',
      overview: 'Card images not loading in trade view',
      currentBehavior: 'When I open a trade, some card images show as broken',
      desiredBehavior: 'All card images should load properly',
      createdAt: new Date().toISOString()
    },
    {
      id: '2',
      userId: 5314143,
      user: {
        id: 5314143,
        username: 'Gosta Dirk',
        email: 'gosta.dirk@example.com',
        avatar: AssetUrls.avatars.avatar2
      },
      feedbackType: 'feature_request',
      featureOverview: 'Add dark mode toggle',
      coreProblem: 'The bright interface is hard on my eyes at night',
      proposedSolution: 'Add a toggle in settings to switch between light and dark themes',
      createdAt: new Date(Date.now() - 86400000).toISOString()
    },
    {
      id: '3',
      userId: 763657,
      user: {
        id: 763657,
        username: 'Angus MacKenzie',
        email: 'angus.mckenzie@example.com',
        avatar: AssetUrls.avatars.avatar3
      },
      feedbackType: 'general',
      sentiment: 'happy',
      generalFeedback: 'Love the new trade interface! Much easier to use than before.',
      responsePreference: 'no',
      createdAt: new Date(Date.now() - 172800000).toISOString()
    }
  ];

  // Use mock data for now, switch to API data when ready
  const rawFeedback = data?.feedback || mockFeedback;
  
  // Apply filters to the feedback data
  const feedback = rawFeedback.filter((item: FeedbackItem) => {
    // Filter out hidden items
    if (hiddenFeedbackIds.has(item.id)) {
      return false;
    }
    
    // Filter by feedback type
    if (feedbackTypeFilter !== 'all' && item.feedbackType !== feedbackTypeFilter) {
      return false;
    }
    
    // Filter by search term
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesUsername = item.user?.username?.toLowerCase().includes(searchLower);
      const matchesEmail = item.user?.email?.toLowerCase().includes(searchLower);
      const matchesOverview = 
        item.overview?.toLowerCase().includes(searchLower) ||
        item.featureOverview?.toLowerCase().includes(searchLower) ||
        item.generalFeedback?.toLowerCase().includes(searchLower) ||
        item.currentBehavior?.toLowerCase().includes(searchLower) ||
        item.desiredBehavior?.toLowerCase().includes(searchLower) ||
        item.coreProblem?.toLowerCase().includes(searchLower) ||
        item.proposedSolution?.toLowerCase().includes(searchLower);
      
      return matchesUsername || matchesEmail || matchesOverview;
    }
    
    return true;
  });

  return (
    <AdminLayout title="Admin Panel - Feedback" currentPath="/admin/feedback">
      {/* @ts-expect-error - React 19 types issue with children */}
      <div className="feedback-page">
        <div className="feedback-header">
          <h2 className="feedback-title">User Feedback</h2>
          <div className="feedback-controls">
            <input
              type="text"
              placeholder="Search by username or content..."
              className="feedback-search-input"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <select
              className="feedback-filter-dropdown"
              value={feedbackTypeFilter}
              onChange={(e) => setFeedbackTypeFilter(e.target.value as FeedbackType | 'all')}
            >
              <option value="all">All Types</option>
              <option value="bug_report">Bug Reports</option>
              <option value="feature_request">Feature Requests</option>
              <option value="general">General Feedback</option>
            </select>
          </div>
        </div>

        <div className="feedback-table-container">
          <table className="feedback-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Email</th>
                <th>Type</th>
                <th>Overview</th>
                <th>Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {feedback.map((item: FeedbackItem) => {
                const avatarPlaceholder = getAvatarPlaceholder(item.user?.username);
                const hasValidAvatar = item.user?.avatar && 
                                      item.user.avatar !== '' && 
                                      item.user.avatar !== 'null' && 
                                      item.user.avatar !== 'undefined';
                
                return (
                  <tr key={item.id}>
                    <td>
                      <div className="feedback-user-cell">
                        {hasValidAvatar && item.user?.avatar ? (
                          <img 
                            src={item.user.avatar} 
                            alt={item.user?.username}
                            className="feedback-user-avatar"
                            onError={(e) => {
                              // Hide broken image and show placeholder
                              const target = e.currentTarget;
                              target.style.display = 'none';
                              const placeholder = target.nextElementSibling as HTMLElement;
                              if (placeholder) {
                                placeholder.style.display = 'flex';
                              }
                            }}
                          />
                        ) : null}
                        {avatarPlaceholder ? (
                          <div 
                            className="feedback-user-avatar feedback-user-avatar-placeholder"
                            style={{ 
                              backgroundColor: avatarPlaceholder.color,
                              display: hasValidAvatar ? 'none' : 'flex'
                            }}
                          >
                            <span className="feedback-avatar-letter">
                              {avatarPlaceholder.letter}
                            </span>
                          </div>
                        ) : (
                          <div 
                            className="feedback-user-avatar feedback-user-avatar-placeholder"
                            style={{ 
                              backgroundColor: '#8409D3',
                              display: hasValidAvatar ? 'none' : 'flex'
                            }}
                          >
                            <span className="feedback-avatar-letter">?</span>
                          </div>
                        )}
                        <span>{item.user?.username || 'Unknown User'}</span>
                      </div>
                    </td>
                  <td>
                    <span className="feedback-email">{item.user?.email || 'No email'}</span>
                  </td>
                  <td>
                    <span 
                      className="feedback-type-badge"
                      style={{ backgroundColor: getFeedbackTypeColor(item.feedbackType) }}
                    >
                      {getFeedbackTypeLabel(item.feedbackType)}
                    </span>
                  </td>
                  <td>
                    <div className="feedback-overview">
                      {getOverviewText(item)}
                      {item.feedbackType === 'general' && item.sentiment && (
                        <span className="feedback-sentiment-inline">
                          {' '}{getSentimentEmoji(item.sentiment)}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="feedback-date">
                      {formatDate(item.createdAt)}
                    </span>
                  </td>
                  <td>
                    {!viewedFeedbackIds.has(item.id) && (
                      <span className="feedback-status feedback-status-new">
                        New
                      </span>
                    )}
                  </td>
                  <td>
                    <div className="feedback-actions">
                      <button 
                        className="feedback-view-btn"
                        onClick={() => handleViewFeedback(item)}
                      >
                        View
                      </button>
                      <button 
                        className="feedback-close-btn"
                        onClick={() => handleHideFeedback(item.id)}
                        title="Hide this feedback"
                      >
                        ×
                      </button>
                    </div>
                  </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {feedback.length === 0 && !isLoading && (
          <div className="feedback-empty">
            No feedback found. Adjust your filters or check back later.
          </div>
        )}

        {isLoading && (
          <div className="feedback-loading">Loading feedback...</div>
        )}

        {error && (
          <div className="feedback-error">Error loading feedback. Please try again.</div>
        )}

        {/* Pagination */}
        {data && data.hasMore && (
          <div className="feedback-pagination">
            <button 
              className="pagination-btn"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </button>
            <span className="pagination-info">Page {page}</span>
            <button 
              className="pagination-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={!data.hasMore}
            >
              Next
            </button>
          </div>
        )}

        {/* Feedback Detail Modal */}
        {isDetailModalOpen && selectedFeedback && (() => {
          const modalAvatarPlaceholder = getAvatarPlaceholder(selectedFeedback.user?.username);
          const modalHasValidAvatar = selectedFeedback.user?.avatar && 
                                      selectedFeedback.user.avatar !== '' && 
                                      selectedFeedback.user.avatar !== 'null' && 
                                      selectedFeedback.user.avatar !== 'undefined';
          
          return (
            <div className="feedback-detail-modal-overlay" onClick={() => setIsDetailModalOpen(false)}>
              <div className="feedback-detail-modal" onClick={(e) => e.stopPropagation()}>
                <div className="feedback-detail-header">
                  <h3>Feedback Details</h3>
                  <button className="feedback-detail-close" onClick={() => setIsDetailModalOpen(false)}>
                    ×
                  </button>
                </div>
                <div className="feedback-detail-content">
                  <div className="feedback-detail-row">
                    <label>User:</label>
                    <div className="feedback-detail-user">
                      {modalHasValidAvatar && selectedFeedback.user?.avatar ? (
                        <img 
                          src={selectedFeedback.user.avatar} 
                          alt={selectedFeedback.user?.username}
                          className="feedback-detail-avatar"
                          onError={(e) => {
                            // Hide broken image and show placeholder
                            const target = e.currentTarget;
                            target.style.display = 'none';
                            const placeholder = target.nextElementSibling as HTMLElement;
                            if (placeholder) {
                              placeholder.style.display = 'flex';
                            }
                          }}
                        />
                      ) : null}
                      {modalAvatarPlaceholder ? (
                        <div 
                          className="feedback-detail-avatar feedback-detail-avatar-placeholder"
                          style={{ 
                            backgroundColor: modalAvatarPlaceholder.color,
                            display: modalHasValidAvatar ? 'none' : 'flex'
                          }}
                        >
                          <span className="feedback-avatar-letter">
                            {modalAvatarPlaceholder.letter}
                          </span>
                        </div>
                      ) : (
                        <div 
                          className="feedback-detail-avatar feedback-detail-avatar-placeholder"
                          style={{ 
                            backgroundColor: '#8409D3',
                            display: modalHasValidAvatar ? 'none' : 'flex'
                          }}
                        >
                          <span className="feedback-avatar-letter">?</span>
                        </div>
                      )}
                      <div>
                        <div>{selectedFeedback.user?.username || 'Unknown User'}</div>
                        <div className="feedback-detail-email">{selectedFeedback.user?.email}</div>
                      </div>
                    </div>
                  </div>
                
                <div className="feedback-detail-row">
                  <label>Type:</label>
                  <span className="feedback-type-badge" style={{ backgroundColor: getFeedbackTypeColor(selectedFeedback.feedbackType) }}>
                    {getFeedbackTypeLabel(selectedFeedback.feedbackType)}
                  </span>
                </div>

                <div className="feedback-detail-row">
                  <label>Submitted:</label>
                  <span>{formatDate(selectedFeedback.createdAt)}</span>
                </div>

                {/* Bug Report Fields */}
                {selectedFeedback.feedbackType === 'bug_report' && (
                  <>
                    <div className="feedback-detail-row">
                      <label>Overview:</label>
                      <p>{selectedFeedback.overview || 'No overview provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Current Behavior:</label>
                      <p>{selectedFeedback.currentBehavior || 'Not provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Desired Behavior:</label>
                      <p>{selectedFeedback.desiredBehavior || 'Not provided'}</p>
                    </div>
                  </>
                )}

                {/* Feature Request Fields */}
                {selectedFeedback.feedbackType === 'feature_request' && (
                  <>
                    <div className="feedback-detail-row">
                      <label>Feature Overview:</label>
                      <p>{selectedFeedback.featureOverview || 'No overview provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Core Problem:</label>
                      <p>{selectedFeedback.coreProblem || 'Not provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Proposed Solution:</label>
                      <p>{selectedFeedback.proposedSolution || 'Not provided'}</p>
                    </div>
                  </>
                )}

                {/* General Feedback Fields */}
                {selectedFeedback.feedbackType === 'general' && (
                  <>
                    <div className="feedback-detail-row">
                      <label>Sentiment:</label>
                      <span className="feedback-sentiment-large">
                        {getSentimentEmoji(selectedFeedback.sentiment)}
                        {' '}
                        {selectedFeedback.sentiment}
                      </span>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Feedback:</label>
                      <p>{selectedFeedback.generalFeedback || 'No feedback provided'}</p>
                    </div>
                    <div className="feedback-detail-row">
                      <label>Response Preference:</label>
                      <span>{selectedFeedback.responsePreference || 'No preference'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          );
        })()}
      </div>
    </AdminLayout>
  );
};

export default FeedbackPage;